/* index.js */

/**
 * @module auditpolicyeditor/schemas
 *
 * Ajv schemas + entry-builders for the audit-policy JSON edit mode. All four
 * schemas live in this file so callers can grab whichever combination they
 * need without juggling imports.
 *
 *   - auditPolicySchema   — top-level policy shape (name/description/permissionLevel/rules).
 *   - ruleSchema          — per-rule shape; names preserved, ref values labelized.
 *   - ifExpressionSchema  — MongoDB-form expression tree for `ifExpression`.
 *   - queryBuilderSchema  — queryBuilder-form expression tree for `then/elseExpression`.
 *
 * The `buildPolicySchemaEntries` / `buildRuleSchemaEntries` factories assemble
 * the {schema, pointer, label}[] array that the JSON editor's `setSchemas()`
 * API consumes. Pointers are stamped only for positions that actually exist
 * in the current document — absent keys would produce confusing
 * top-of-file annotations.
 */

// Top-level Ajv schema for an audit policy seen through the JSON editor.
// Only the four user-editable fields are validated here. Any other top-level
// keys are tolerated (additionalProperties:true) so users can paste JSON that
// came out of an export, etc; the JSON→form transfer step strips them.
// Per-rule shape is enforced by ruleSchema applied via JSON pointer
// (/rules/<n>) so error messages land on the right line.
const auditPolicySchema = {
    type: "object",
    required: ["name", "rules"],
    properties: {
        name: {type: "string", minLength: 1},
        description: {type: ["string", "null"]},
        permissionLevel: {type: ["string", "null"]},
        rules: {type: "array"},
    },
    additionalProperties: true,
};

// Ajv schema for an audit policy rule. Reference properties keep their names
// across the JSON round-trip; only the value is labelized (ruleUuidsToLabels
// swaps the UUID value for its label and back). buildDynamicSchemas tightens
// `commandSet` / `conceptItemId` / `rsiId` to the runtime label enum.
//
// Conditional `if` keywords enforce that:
//   - `ifExpression` is required when `type` is "IF_THEN" or "IF_THEN_ELSE".
//   - `elseExpression` is required when `type` is "IF_THEN_ELSE".
//
// Expression bodies are NOT validated here — they are validated separately
// via the ifExpression / queryBuilder schemas applied by JSON pointer at the
// appropriate paths.
const COMMAND_SET_RESULT_SCHEMA = {
    type: "object",
    properties: {
        type: {
            enum: [
                "all",
                "conceptItems",
                "restSyncItems",
                "cliCommands",
                "getUrls",
                "basicMode",
                null,
            ],
        },
        command: {type: ["string", "null"]},
        getUrl: {type: ["string", "null"]},
        // Optional entity references — cleared values arrive as null. The
        // property name is kept across the JSON round-trip; only the value is
        // labelized (buildDynamicSchemas tightens these to the label enum).
        conceptItemId: {type: ["string", "null"]},
        restSyncItemId: {type: ["string", "null"]},
    },
    additionalProperties: true,
};

const ruleSchema = {
    type: "object",
    required: ["name", "type", "severity", "enable"],
    properties: {
        name: {type: "string", minLength: 1},
        description: {type: ["string", "null"]},
        enable: {type: "boolean"},
        severity: {type: "string", enum: ["INFO", "MINOR", "MAJOR", "CRITICAL"]},
        // Optional fields — cleared as "" or null by the form/save path or
        // absent/null in backend data. Enum values are still checked when a
        // non-null value is present.
        os: {type: ["string", "null"]},
        source: {enum: ["LATEST", "DEFAULT", null]},
        scope: {type: ["string", "null"]},
        scopeSeparateResults: {type: ["boolean", "null"]},
        startRegex: {type: ["string", "null"]},
        endRegex: {type: ["string", "null"]},
        type: {type: "string", enum: ["SIMPLE", "IF_THEN", "IF_THEN_ELSE"]},

        // Optional entity references — cleared as null. Name preserved across
        // the round-trip; value labelized (buildDynamicSchemas adds the enum).
        commandSet: {type: ["string", "null"]},
        conceptItemId: {type: ["string", "null"]},
        rsiId: {type: ["string", "null"]},

        commandSetResult: {anyOf: [{type: "null"}, COMMAND_SET_RESULT_SCHEMA]},

        // Optional/absent depending on `type`; backend may store null. The
        // shape of a non-null expression is validated by the dedicated
        // ifExpression / queryBuilder schemas applied via JSON pointer.
        ifExpression: {type: ["object", "null"]},
        thenExpression: {type: ["object", "null"]},
        elseExpression: {type: ["object", "null"]},

        expected: {},
        required: {},
    },
    additionalProperties: true,
    allOf: [
        {
            if: {properties: {type: {const: "IF_THEN"}}, required: ["type"]},
            then: {required: ["ifExpression", "thenExpression"]},
        },
        {
            if: {properties: {type: {const: "IF_THEN_ELSE"}}, required: ["type"]},
            then: {required: ["ifExpression", "thenExpression", "elseExpression"]},
        },
        {
            // SIMPLE rules have no if/else branch — `thenExpression` is their
            // sole match expression, so only ifExpression/elseExpression are
            // forbidden (the renderer sets both to undefined for SIMPLE). A
            // `false` property schema forbids the key even though
            // additionalProperties is true.
            if: {properties: {type: {const: "SIMPLE"}}, required: ["type"]},
            then: {
                properties: {
                    ifExpression: false,
                    elseExpression: false,
                },
            },
        },
    ],
};

// Ajv schema for the MongoDB-style `ifExpression` of an audit policy rule.
// Recursive: top-level $and / $or arrays of subexpressions, with leaf nodes
// shaped as `{<field>: {<op>: <value>}}`. Field names may be plain device
// properties or `customFieldValues.<label-or-uuid>` paths.
const ifExpressionSchema = {
    definitions: {
        // A leaf is a property→operator-object map. We forbid $-prefixed keys
        // here so groups ($and / $or) and leaves are mutually exclusive — that
        // way a single anyOf branch matches and Ajv produces clean error
        // messages instead of "matched multiple branches" noise.
        leaf: {
            type: "object",
            minProperties: 1,
            propertyNames: {not: {pattern: "^\\$"}},
            // Operator-object keys (e.g. {$eq: ...}) are validated by
            // expressionFieldValidator, which can name the offending operator —
            // the schema would only produce anyOf "must match" noise here.
            additionalProperties: {
                anyOf: [
                    {type: "object"},
                    {type: "string"},
                    {type: "number"},
                    {type: "boolean"},
                    {type: "array"},
                    {type: "null"},
                ],
            },
        },
    },
    type: "object",
    anyOf: [
        {
            type: "object",
            required: ["$and"],
            properties: {
                $and: {type: "array", items: {$ref: "#"}},
            },
            additionalProperties: false,
        },
        {
            type: "object",
            required: ["$or"],
            properties: {
                $or: {type: "array", items: {$ref: "#"}},
            },
            additionalProperties: false,
        },
        {$ref: "#/definitions/leaf"},
    ],
};

// Ajv schema for the queryBuilder-style `thenExpression` / `elseExpression` of
// an audit policy rule. Recursive: groups carry `condition: "AND"|"OR"` and a
// `rules` array containing either a leaf rule or another group.
// Note: data uses `condition` (not `combinator`).
const queryBuilderSchema = {
    definitions: {
        leaf: {
            type: "object",
            required: ["id", "operator"],
            properties: {
                id: {type: "string"},
                field: {type: "string"},
                type: {type: "string"},
                input: {type: "string"},
                operator: {type: "string"},
                value: {
                    oneOf: [
                        {type: "string"},
                        {type: "number"},
                        {type: "boolean"},
                        {type: "array"},
                        {type: "null"},
                    ],
                },
            },
            additionalProperties: true,
        },
    },
    type: "object",
    required: ["condition", "rules"],
    properties: {
        condition: {type: "string", enum: ["AND", "OR"]},
        rules: {
            type: "array",
            items: {
                oneOf: [{$ref: "#/definitions/leaf"}, {$ref: "#"}],
            },
        },
        not: {type: "boolean"},
        valid: {type: "boolean"},
    },
    additionalProperties: true,
};

// `dynamicSchemas` (optional, from buildDynamicSchemas) lets callers
// substitute runtime-tightened copies of the base schemas — e.g. a
// ruleSchema with `os`/`commandSet`/`conceptItemId`/etc. enums populated
// from the currently-open policy. Falls back to the module-level base
// schemas so non-policy consumers and tests keep working.
const _resolveSchemas = (dynamicSchemas) => ({
    auditPolicy: (dynamicSchemas && dynamicSchemas.auditPolicySchema) || auditPolicySchema,
    rule: (dynamicSchemas && dynamicSchemas.ruleSchema) || ruleSchema,
    ifExpression: (dynamicSchemas && dynamicSchemas.ifExpressionSchema) || ifExpressionSchema,
    queryBuilder: (dynamicSchemas && dynamicSchemas.queryBuilderSchema) || queryBuilderSchema,
});

const _ruleEntries = (rule, basePointer, schemas) => {
    const entries = [{schema: schemas.rule, pointer: basePointer, label: "rule"}];
    if (rule && rule.ifExpression) {
        entries.push({
            schema: schemas.ifExpression,
            pointer: basePointer + "/ifExpression",
            label: "ifExpression",
        });
    }
    if (rule && rule.thenExpression) {
        entries.push({
            schema: schemas.queryBuilder,
            pointer: basePointer + "/thenExpression",
            label: "thenExpression",
        });
    }
    if (rule && rule.elseExpression) {
        entries.push({
            schema: schemas.queryBuilder,
            pointer: basePointer + "/elseExpression",
            label: "elseExpression",
        });
    }
    return entries;
};

const buildPolicySchemaEntries = (policy, dynamicSchemas) => {
    const schemas = _resolveSchemas(dynamicSchemas);
    const entries = [{schema: schemas.auditPolicy, pointer: "", label: "policy"}];
    const rules = (policy && policy.rules) || [];
    rules.forEach((rule, ruleIndex) => {
        entries.push(..._ruleEntries(rule, `/rules/${ruleIndex}`, schemas));
    });
    return entries;
};

const buildRuleSchemaEntries = (rule, dynamicSchemas) =>
    _ruleEntries(rule, "", _resolveSchemas(dynamicSchemas));

module.exports = {
    auditPolicySchema,
    ruleSchema,
    ifExpressionSchema,
    queryBuilderSchema,
    buildPolicySchemaEntries,
    buildRuleSchemaEntries,
};
